import 'ol/ol.css';
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import VectorImageLayer from 'ol/layer/VectorImage';
import {Point, Polygon} from "ol/geom";
import {Feature} from "ol";
import {
  Circle as CircleStyle,
  Fill, Stroke, Style
} from "ol/style";
import { checkValidPolygon } from "../utilities";
import {
  annotationFromPolygon,
  CocoImage,
  ImageLabel,
  ImageLabelTools,
  LabelCategory,
  sectionsFromIntersection,
  textToColor
} from '.';
import {Color, Vector3} from 'three';
import {
  polygon as turfPolygon,
  intersect as turfIntersect
} from '@turf/turf';
import {
  OrthoMosaic,
  AerialMap,
  extentFromPoints
} from '../aerial';
import { LabelContextData, showContextMenu } from '../../../context-menu';
import { CustomMouseEvent } from '../controls';
import { clearAerialTooltip, setAerialTooltip } from '../../viewer';
import { t } from '../../../localization';

const pointRadius = 5.0;
const pointStrokeWidth = 1.0;

class LabelPoint extends Feature<any> {
  public label: OrthoLabel;
  public index;

  constructor(opts, label, index) {
    super(opts);
    this.label = label;
    this.index = index;
  }
}

class LabelPolygon extends Feature<any> {
  public label: OrthoLabel;

  constructor(opts, label) {
    super(opts);
    this.label = label;
  }
}

class Coordinate {
  public array = [];

  constructor(coordinates) {
    this.array = coordinates;
  }

  get x() {
    return this.array[0];
  }

  get y() {
    return this.array[1];
  }

  get point() {
    return this.array;
  }

  update(point) {
    if (point.length === 2) {
      this.array = point;
    }
  }
}

export class OrthoLabel extends ImageLabel {
  public features = [];
  public category: OrthoCategory
  public coordinates: Coordinate[] = [];
  public aerialMap: AerialMap;
  private minCoordinates = 3;
  private styleCache = {};

  constructor(category: OrthoCategory, aerialMap: AerialMap) {
    super();
    this.category = category;
    this.aerialMap = aerialMap;
  }

  get hasEnoughPoints() {
    return this.numPoints >= this.minCoordinates;
  }

  get numPoints() {
    return this.coordinates.length;
  }

  get coords() {
    /** Used for polygon simplification */
    return this.coordinates.map(x => x.point);
  }

  get polygonPointsOnly() {
    return this.coordinates.map(x => x.point);
  }

  get isValid() {
    return checkValidPolygon(this.polygonPointsOnly);
  }

  get map() {
    return this.aerialMap.map;
  }

  get completedSource() {
    return this.category.featuresLayer.getSource();
  }

  get editingSource() {
    return this.category.editingLayer.getSource();
  }

  get featureStyle() {
    return this.getLayerFromCache(true);
  }

  get editingStyle() {
    return this.getLayerFromCache(false);
  }

  clearStyleCache() {
    this.styleCache = {};
  }

  setFeatureStyle() {
    this.features.forEach(feature => {
      if (this.completed) {
        feature.setStyle(this.featureStyle);
      } else {
        feature.setStyle(this.editingStyle);
      }
    });
  }

  getLayerFromCache(checkDefaultStyle) {
    if (checkDefaultStyle) {
      let defaults = this.category.defaultStyleParams;
      let isDefaultStyle = (this.valid === defaults[0])
        && (this.completed === defaults[1])
        && (this.highlighted === defaults[2]);

      if (isDefaultStyle) {
        return null;
      }
    }

    let styleKey = `${this.valid}-${this.completed}-${this.highlighted}`;

    if (!this.styleCache[styleKey]) {
      this.styleCache[styleKey] = this.category.getLayerStyle(
        this.valid, this.completed, this.highlighted);
    }

    return this.styleCache[styleKey];
  }

  updatePoint(point, index) {
    this.coordinates[index].update(point);
  }

  removePoint() {
    this.coordinates.pop();
  }

  removeSpecificPoint(index) {
    this.coordinates.splice(index, 1);
    this.draw();
  }

  pointToScreen(point) {
    return this.map.getPixelFromCoordinate(point.point);
  }

  createCoordData(coordinate) {
    const point = new Vector3(coordinate[0], coordinate[1]);
    const coordArray = this.coordinates.map(coord => {
      return coord.array;
    });

    return {coordArray, point};
  }

  addPoint(point, checkDuplicate=true) {
    const newPoint = new Coordinate(point);
    const oldPoint = this.coordinates[this.coordinates.length - 1];

    if (checkDuplicate && this.duplicatePoint(oldPoint, newPoint)) {
      return true;
    }

    this.coordinates.push(newPoint);
    return false;
  }

  insertPoint(newPoint, idxCoord) {
    let newCoord = new Coordinate(newPoint.toArray());
    this.coordinates.splice(idxCoord+1, 0, newCoord);
    this.draw();
  }

  drawPoints(temporaryPoint) {
    let positions = this.coordinates;
    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint];
    }

    positions.forEach((point, index) => {
      const feature = new LabelPoint({
        geometry: new Point([point.x, point.y])
      }, this, index);

      this.features.push(feature);

      let source = this.completed
        ? this.completedSource
        : this.editingSource;

      source.addFeature(feature);
    });
  }

  drawPolygon(temporaryPoint) {
    let positions = this.polygonPointsOnly;
    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.point];
    }

    if (this.hasEnoughPoints){
      let validPolygon = checkValidPolygon(positions);
      if (this.valid !== validPolygon) {
        this.valid = validPolygon;
        this.category.setCategories();
      }
    }

    let feature = new LabelPolygon({
      geometry: new Polygon([positions])
    }, this);

    this.features.push(feature);

    let source = this.completed
      ? this.completedSource
      : this.editingSource;

    source.addFeature(feature);
  }

  draw(temporaryPoint=null) {
    if (!this.drawable) {
      return;
    }

    this.clear();
    this.drawPoints(temporaryPoint);
    this.drawPolygon(temporaryPoint);
    this.setFeatureStyle();
  }

  clear() {
    const sources = [
      this.completedSource,
      this.editingSource
    ];

    this.features.forEach(x => {
      sources.forEach(source => {
        if (source.hasFeature(x)) {
          source.removeFeature(x);
        }
      });
    });

    this.features = [];
  }

  zoom(): Promise<boolean> {
    let positions = [...this.polygonPointsOnly];
    let extent = extentFromPoints(positions, false);
    this.aerialMap.setMapExtent(extent);
    return Promise.resolve(true);
  }

  setHighlighted(highlight) {
    if (highlight === this.highlighted) {
      return;
    }

    this.highlighted = highlight;
    this.draw();
  }
}

export class OrthoCategory extends LabelCategory {
  private aerialTools: AerialLabelTools;
  public labels: OrthoLabel[] = [];
  public featuresLayer: VectorImageLayer<any>;
  public editingLayer: VectorLayer<any>;

  constructor(aerialTools: AerialLabelTools, name) {
    super(name);
    this.aerialTools = aerialTools;
    this.addMapLayers();
  }

  get aerialMap() {
    return this.aerialTools.aerialMap;
  }

  get map() {
    return this.aerialMap.map;
  }

  get defaultStyleParams() {
    // valid, completed, higlighted
    return [true, true, false];
  }

  darkenedColor(highlighted) {
    let pointColor = this.categoryColor(highlighted, 1.0);
    let dark = new Color(pointColor)
      .multiplyScalar(0.75);

    return `rgb(${dark.r*256}, ${dark.g*256}, ${dark.b*256})`;
  }

  categoryColor(highlighted=false, opacity=0.2) {
    if (highlighted) {
      return `rgba(${255}, ${255}, ${255}, ${opacity})`;
    }

    if (this instanceof TrainingAreaCategory){
      return `rgba(${0}, ${0}, ${0}, ${opacity})`;
    }

    return textToColor(this.name, opacity);
  }

  getLayerStyle(valid=true, completed=true, highlighted=false) {
    let opacity = valid ? 0.2 : 0.0;
    if (this instanceof TrainingAreaCategory) {
      opacity = 0.0;
    }

    return new Style({
      fill: new Fill({
        color: this.categoryColor(highlighted, opacity)
      }),
      stroke: new Stroke({
        lineDash: completed ? undefined : [10, 10],
        color: this.darkenedColor(highlighted),
        width: 2
      }),
      image: new CircleStyle({
        radius: pointRadius,
        stroke: new Stroke({
          width: pointStrokeWidth,
          color: 'rgba(0, 0, 0, 1.0)'
        }),
        fill: new Fill({
          color: this.categoryColor(highlighted, 0.8)
        }),
      })
    });
  }

  addMapLayers() {
    this.featuresLayer = new VectorImageLayer({
      zIndex: 1,
      source: new VectorSource({
        features: [],
        wrapX: false
      }),
      style: this.getLayerStyle()
    });

    this.map.addLayer(this.featuresLayer);

    this.editingLayer = new VectorLayer({
      zIndex: 1,
      source: new VectorSource({
        features: [],
        wrapX: false
      }),
      style: this.getLayerStyle(),
      updateWhileInteracting: false,
      updateWhileAnimating: false
    });

    this.map.addLayer(this.editingLayer);
  }

  setCategories() {
    this.aerialTools.imageLabelTools.setCategories();
  }

  updateColor() {
    this.featuresLayer.setStyle(this.getLayerStyle());
    this.editingLayer.setStyle(this.getLayerStyle());

    this.labels.forEach(label => {
      label.clearStyleCache();
      label.setFeatureStyle();
    });
  }

  addNewLabel(drawLabel) {
    let label = new OrthoLabel(this, this.aerialMap);
    label.setDrawable(drawLabel);
    this.labels.push(label);

    return label;
  }
}

export class TrainingAreaLabel extends OrthoLabel {
  constructor(category: OrthoCategory, aerialMap: AerialMap) {
    super(category, aerialMap);
  }

  polygonsInsideImage(image: OrthoMosaic) {
    // Find the part of the training area label that intersects with this image
    let intersection = image.intersectAerialPoints(this.polygonPointsOnly);
    let intersectionData = sectionsFromIntersection(intersection, false);

    return intersectionData.map((arr) => {
      return image.aerialToPixels(arr);
    });
  }
}

class TrainingAreaCategory extends OrthoCategory {
  public labels: TrainingAreaLabel[] = [];

  constructor(aerialTools: AerialLabelTools, name) {
    super(aerialTools, name);
  }

  addNewLabel(drawLabel) {
    let label = new TrainingAreaLabel(this, this.aerialMap);
    label.setDrawable(drawLabel);
    this.labels.push(label);

    return label;
  }
}

export class AerialLabelTools {
  public type = "ortho";
  public enabled = false;
  public imageLabelTools: ImageLabelTools;
  public setPointDelete;
  public setPointAdd;
  public setCategoryList;
  private temporaryPoint: Coordinate;
  public trainingArea: TrainingAreaCategory;
  private polygonHover: LabelPolygon = null;
  private pointHover: LabelPoint = null;

  constructor(imageLabelTools: ImageLabelTools, props) {
    const {setCategoryList} = props;

    this.imageLabelTools = imageLabelTools;
    this.setCategoryList = setCategoryList;
  }

  get startTooltip() {
    return [
      t('measure.click-to-start-drawing-polygon')
    ];
  }

  get dragTooltip() {
    return [
      t('measure.click-and-hold-to-move-vertex'),
      t('measure.right-click-to-select-point')
    ];
  }

  get highlightTooltip() {
    return [
      t('measure.right-click-to-select-label'),
      t('measure.ctrl-right-click-to-select-multiple-labels')
    ];
  }

  get categories() {
    return this.imageLabelTools.categories
      .filter(x => x instanceof OrthoCategory) as OrthoCategory[];
  }

  get viewer() {
    return this.imageLabelTools.viewer;
  }

  get aerialMap() {
    return this.viewer.minimap;
  }

  get map() {
    return this.aerialMap.map;
  }

  get drawing() {
    let label = this.activeLabel;
    if (!label || label.completed) {
      return false;
    }

    if (label.coordinates.length === 0) {
      return false;
    }

    return true;
  }

  get activeLabel() {
    let selected = null;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.selected) {
          selected = label;
        }
      });
    });

    this.trainingArea?.labels.forEach(label => {
      if (label.selected) {
        selected = label;
      }
    });

    return selected;
  }

  get numberLabels() {
    let count = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => count++));
    return count;
  }

  get moreTooltip() {
    return [
      t("labels.click-to-add-more-points", {
        count: this.numPoints
      }),
      t('labels.delete-key-to-remove-last-point'),
      ...(this.numPoints > 1) ? [t('labels.double-click-to-finish')] : [],
      ...(this.numPoints > 2) ? [t('labels.press-enter-to-exit')] : [],
      ...this.activeLabel.editing ? [] : [t('labels.right-click-to-cancel')]
    ];
  }

  get invalidTooltip() {
    return [
      t('labels.polygon-is-invalid-due-to-self-intersection'),
      ...this.moreTooltip
    ];
  }

  get numPoints() {
    let pointCount = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          pointCount = label.numPoints;
        }
      })
    );

    this.trainingArea?.labels.forEach(label => {
      if (label.selected) {
        pointCount = label.numPoints;
      }
    });

    return pointCount;
  }

  get selected() {
    let selected = false;
    this.categories.forEach(category =>
      category.labels.forEach(label =>
        selected = (selected || label.selected)
      )
    );

    this.trainingArea?.labels.forEach(label =>
      selected = (selected || label.selected)
    );

    return selected;
  }

  get visibleOrthos() {
    let orthoList = this.viewer.orthoList;
    return orthoList.orthos.filter(x => x.visible);
  }

  get activeImagePaths() {
    return this.visibleOrthos.map(ortho => ortho.path);
  }

  get loadedImagePaths() {
    let orthoList = this.viewer.orthoList;
    return orthoList.orthos.map(ortho => ortho.path);
  }

  get polygonFeaturesForExtent() {
    let features = [];
    this.categories.forEach(category => {
      let source = category.featuresLayer.getSource();
      let extent = this.map.getView().calculateExtent(this.map.getSize());
      let sourceFeatures = source.getFeaturesInExtent(extent);
      let points = sourceFeatures.filter(x => x instanceof LabelPolygon);
      features = [...features, ...points];
    });

    return features;
  }

  get pointFeaturesForExtent() {
    let features = [];

    this.categories.forEach(category => {
      let source = category.featuresLayer.getSource();
      let extent = this.map.getView().calculateExtent(this.map.getSize());
      let sourceFeatures = source.getFeaturesInExtent(extent);
      let points = sourceFeatures.filter(x => x instanceof LabelPoint);
      features = [...features, ...points];
    });

    if (this.trainingArea) {
      let source = this.trainingArea.featuresLayer.getSource();
      let extent = this.map.getView().calculateExtent(this.map.getSize());
      let sourceFeatures = source.getFeaturesInExtent(extent);
      let points = sourceFeatures.filter(x => x instanceof LabelPoint);
      features = [...features, ...points];
    }

    return features;
  }

  get trainingLabels() {
    return this.trainingArea
      ? this.trainingArea.labels.filter(label => label.isValid)
      : [];
  }

  completeLabel(label: OrthoLabel) {
    this.resetTemporaryPoint();
    this.imageLabelTools.completeLabel(label.id);
    this.markLastPointDraggable(label);
  }

  updateDraggablePoint(event) {
    let {label, index} = this.pointHover;
    label.updatePoint(event.coordinate, index);
    label.draw();

    if (label.score < 1.0) {
      label.setScore(1.0);
      this.imageLabelTools.setCategories();
    }
  }

  markLastPointDraggable(label: OrthoLabel) {
    let pointFeatures = label.features.filter(x => x instanceof LabelPoint);
    let lastPoint = pointFeatures[pointFeatures.length - 1];
    this.setPointHover(lastPoint);
  }

  cancelLabel() {
    if (this.activeLabel && !this.activeLabel.editing) {
      this.imageLabelTools.deleteLabel(this.activeLabel.id);
      this.setToolTipText();
      this.drawAll();
    }
  }

  setState(state) {
    this.enabled = state;
    clearAerialTooltip();
  }

  setPointHover(feature) {
    this.pointHover = feature;

    if (feature) {
      this.setPointGrabTooltip();
    }

    const canDrag = feature ? false : true;
    this.aerialMap.setDragState(canDrag);
  }

  setPolygonHover(feature) {
    this.polygonHover = feature;
  }

  setTrainingArea(category: TrainingAreaCategory) {
    this.trainingArea = category;
  }

  deleteTrainingArea() {
    this.trainingArea.labels.forEach(label => {
      label.destroy();
    });

    this.trainingArea = null;
  }

  openContextMenu(event, feature) {
    let pointID = (feature instanceof LabelPoint) ? feature.index : null;
    let labelID = feature.label.id;
    let coordinate = this.map.getCoordinateFromPixel([
      event.offsetX, event.offsetY
    ]);

    const labelData = {
      coordinate,
      pointID,
      labelID
    } as LabelContextData;

    // TODO: create labelCoord ? (same for scene view)

    showContextMenu(event, true, {labelData});
  }

  onMapClick(event) {
    if (!this.enabled) return false;

    let label = this.activeLabel;
    if (!label) {
      return false;
    }

    let duplicatePoint = label.addPoint(event.coordinate);
    if (duplicatePoint && label.hasEnoughPoints) {
      this.completeLabel(label);
      return true;
    }

    this.setToolTipText();
    this.resetTemporaryPoint();
    this.drawActiveLabel();
    return true;
  }

  onDoubleClick(event) {
    if (!this.enabled) return false;

    // Restrict double click zoom
    event.preventDefault();
  }

  onMouseMove(event) {
    if (!this.enabled) return false;

    this.resetTemporaryPoint();

    // Drawing new points
    if (this.selected) {
      this.temporaryPoint = new Coordinate(event.coordinate);
      this.setToolTipText();
      this.drawActiveLabel();
      return;
    }

    if (!event.dragging) {
      this.checkFeatureHover(event);
    }

    if (this.pointHover) {
      this.setPointGrabTooltip();
      if (event.dragging) {
        this.updateDraggablePoint(event);
        return;
      }
    } else if (this.polygonHover) {
      this.setPolygonHoverTooltip();
    } else {
      this.setToolTipText();
    }
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (!this.enabled) return false;

    if (event.isRightClick) {
      if (this.drawing) {
        this.cancelLabel();
        return true;
      }

      return this.onRightClick(event);
    }

    return false;
  }

  onRightClick(event) {
    const oneHighlighted = this.imageLabelTools.oneHighlighted;
    const noneHighlighted = this.imageLabelTools.noneHighlighted;

    if (noneHighlighted && !event.ctrlKey) {
      let feature = this.highlightLabel();
      if (feature) {
        this.openContextMenu(event, feature);
        return true;
      }
    }

    if (oneHighlighted && !event.ctrlKey) {
      this.imageLabelTools.unHighlightAll();
      let feature = this.highlightLabel();
      if (feature) {
        this.openContextMenu(event, feature);
        return true;
      }
    }

    if (event.ctrlKey) {
      this.highlightLabel();
      return true;
    }

    return false;
  }

  checkFeatureHover(event) {
    if (event instanceof MouseEvent) {
      const pixel = [event.offsetX, event.offsetY];
      const coordinate = this.map.getCoordinateFromPixel(pixel);
      event = {pixel, coordinate};
    }

    let selectedPoint = this.getFeatureAtPixel(event,
      this.pointFeaturesForExtent);

    let selectedPolygon = this.getFeatureAtPixel(event,
      this.polygonFeaturesForExtent);

    this.setPointHover(selectedPoint);
    this.setPolygonHover(selectedPolygon);
  }

  getFeatureAtPixel(event, features) {
    const mousePixel = event.pixel;
    const mouseCoord = event.coordinate;
    const buffer = 10;

    const x = mousePixel[0];
    const y = mousePixel[1];

    const mouseBuffer = this.map.getCoordinateFromPixel([x, y])[0]
      - this.map.getCoordinateFromPixel([x - buffer, y])[0];

    for (let feature of features) {
      // NOTE: We know the geometry exists, so this is faster than
      // calling feature.getGeometry() or feature.get("geometry")
      const geometry = feature.values_.geometry;

      const pointExtent = geometry.getExtent();
      const notVisible = (pointExtent[0] > (mouseCoord[0] + mouseBuffer))
        || (pointExtent[2] < (mouseCoord[0] - mouseBuffer))
        || (pointExtent[1] > (mouseCoord[1] + mouseBuffer))
        || (pointExtent[3] < (mouseCoord[1] - mouseBuffer));

      if (notVisible) {
        continue;
      }

      if (feature instanceof LabelPolygon) {
        const intersect = geometry.intersectsCoordinate(mouseCoord);
        if (intersect) {
          return feature;
        }
      }

      if (feature instanceof LabelPoint) {
        const coord = geometry.flatCoordinates;
        const pixel = this.map.getPixelFromCoordinate(coord);
        const dx = pixel[0] - mousePixel[0];
        const dy = pixel[1] - mousePixel[1];
        const dist = Math.sqrt(dx*dx + dy*dy);

        // Check using pointRadius + outline
        const maxDistance = pointRadius + pointStrokeWidth;
        const intersect = dist <= maxDistance;
        if (intersect) {
          return feature;
        }
      }
    };
  }

  highlightLabel() {
    let selected = null;

    if (this.pointHover) {
      selected = this.pointHover;
    } else if (this.polygonHover) {
      selected = this.polygonHover;
    } else {
      return;
    }

    let label = selected.label;
    label.setHighlighted(!label.highlighted);
    this.imageLabelTools.setCategories();

    return selected;
  }

  setToolTipText() {
    if (!this.activeLabel) {
      clearAerialTooltip();
      return;
    }

    const started = (this.numPoints === 0);

    let helpMsg = started
      ? this.startTooltip
      : this.moreTooltip;

    helpMsg = this.activeLabel.valid
      ? helpMsg
      : this.invalidTooltip;

    setAerialTooltip(helpMsg);
  }

  setPointGrabTooltip() {
    setAerialTooltip(this.dragTooltip);
  }

  setPolygonHoverTooltip() {
    setAerialTooltip(this.highlightTooltip);
  }

  drawAll() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.draw(this.temporaryPoint);
      });
    });

    this.trainingArea?.labels.forEach(label => {
      label.draw(this.temporaryPoint);
    });
  }

  drawActiveLabel() {
    if (!this.activeLabel) {
      return;
    }

    this.activeLabel.draw(this.temporaryPoint);

  }

  getNewCategory(name) {
    return new OrthoCategory(this, name);
  }

  getNewTrainingArea() {
    return new TrainingAreaCategory(this, "training");
  }

  deletePoint() {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.removePoint();
          this.setToolTipText();
          this.drawActiveLabel();
        }
      })
    );

    this.imageLabelTools.setCategories();
  }

  deleteSpecificPoint(id) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {

        if (label.highlighted && (label.numPoints > 3)) {
          label.removeSpecificPoint(id);
          this.imageLabelTools.unHighlight(label.id);
        }
      })
    );

    this.trainingArea?.labels.forEach(label => {
      if (label.highlighted && (label.numPoints > 3)) {
        label.removeSpecificPoint(id);
        this.imageLabelTools.unHighlight(label.id);
      }
    });

    this.imageLabelTools.setCategories();
  }

  resetTemporaryPoint() {
    this.temporaryPoint = null;
  }

  calculatePolygonPixels = (polygonPoints, metadata) => {
    let pixelPolygon = [];

    polygonPoints.forEach(point => {
      let pixelPoint = [((point[0] - metadata.x_offset)/metadata.x_scale),
        ((point[1] - metadata.y_offset)/metadata.y_scale)];
      pixelPolygon.push(pixelPoint);
    });
    return pixelPolygon;
  }

  jsonImageObj() {
    let images = this.visibleOrthos.map((ortho, imageIndex) => {
      return {
        height: ortho.originalSize[1],
        width: ortho.originalSize[0],
        id: imageIndex,
        file_name: ortho.orthoName,
        metadata: ortho.metadata
      } as CocoImage;
    });

    return images;
  }

  getTrainingAnnotations = () => {
    let labelIndex = 0;
    let annotations = [];

    if (this.trainingLabels.length === 0) {
      return annotations;
    }

    this.visibleOrthos.forEach((image, imageID) => {
      this.trainingLabels.forEach((trainingLabel, trainingLabelIndex) => {
        trainingLabel.polygonsInsideImage(image).forEach(polygon => {
          let mergeID = trainingLabelIndex+1;
          let trainingLabel = annotationFromPolygon(polygon,
            labelIndex, 0, imageID, mergeID);

          annotations.push(trainingLabel);
          labelIndex += 1;
        });
      });
    });

    return annotations;
  }

  getLabelAnnotations() {
    let labelIndex = 0;
    let annotations = [];

    this.visibleOrthos.forEach((image, imageID) => {
      let boundsToCheck = [];

      if (this.trainingLabels.length === 0) {
        // Bounds are the entire image
        let boundsPolygon = [...image.cornerPixels, image.cornerPixels[0]];
        boundsToCheck.push({imageID, boundsPolygon});
      } else {
        // Bounds are training area polygons for this image
        this.trainingLabels.forEach(trainingLabel => {
          trainingLabel.polygonsInsideImage(image).forEach(boundsPolygon => {
            boundsToCheck.push({imageID, boundsPolygon});
          });
        });
      }

      this.categories.forEach((category, categoryID) => {
        category.labels.forEach((label, labelID) => {
          if (!label.valid) {
            return;
          }

          // Check for label inside each boundary polygon
          boundsToCheck.forEach(({imageID, boundsPolygon}) => {
            let labelPolygon = this.visibleOrthos[imageID]
              .aerialToPixels(label.polygonPointsOnly);
            labelPolygon = [...labelPolygon, labelPolygon[0]];

            let intersection = turfIntersect(
              turfPolygon([boundsPolygon]),
              turfPolygon([labelPolygon])
            );

            let intersectionData = sectionsFromIntersection(intersection);
            intersectionData.forEach((polygon) => {
              let mergeID = labelID + 1;
              let annotation = annotationFromPolygon(polygon, labelIndex,
                categoryID, imageID, mergeID, label.score, label.text, label.textScore,
                label.findText);

              annotations.push(annotation);
              labelIndex += 1;
            });
          });
        });
      });
    });

    return annotations;
  };

  get activeImages() {
    return [];
  }
}
